/**
 * THE ESCROW TRADE ROOM (FR-U-TXN-002, 008, 012).
 *
 * ## Two layouts, deliberately different
 *
 * A trade is on one of two release paths and they are genuinely different deals. A shared layout
 * with a swapped sentence would imply they are one deal with a setting, and the thing a participant
 * most needs to know is which of the two they are in:
 *
 *   RAIL SETTLED           we see the payment land, and release. Nobody has to confirm.
 *   COUNTERPARTY CONFIRMED the seller confirms. If they do not, the buyer disputes and the asset
 *                          stays locked until somebody decides.
 *
 * The step lists are different lengths for the same reason: the manual path genuinely has one more
 * thing that must happen, and flattening that would be a lie of layout rather than of words.
 *
 * ## What this screen must never imply
 *
 * That waiting resolves anything. A timed-out step escalates to DISPUTE and the escrow stays held
 * (FR-U-TXN-008, ADR 0009). The countdown here is a prompt, never a promise: it does not release to
 * the buyer and it does not refund to the seller, because both directions of the clock are somebody
 * else's money.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LipaApiError, api, type P2PMessage, type P2PTrade, type TradeStep } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';

type Path = 'rail_settled' | 'counterparty_confirmed';

/**
 * TRADE CHAT — the buyer and seller coordinate the payment here, and only here. On-platform on
 * purpose (they never swap a phone number, so they never leave). Sockets would be nicer, but a
 * short poll is honest and robust for a two-person room whose lifetime is one trade; it mirrors the
 * belt-and-suspenders fallback the ATS chat uses. Only a party to the trade can read or post — the
 * server enforces it, so a wrong id gets a 403, not somebody else's conversation.
 */
function ChatPanel({ tradeId }: { tradeId: string }) {
  const [messages, setMessages] = useState<P2PMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.p2pMessages(tradeId);
      setMessages(res.messages);
      void api.markP2PRead(tradeId);
    } catch {
      /* a transient read failure just means the next poll retries */
    }
  }, [tradeId]);

  useEffect(() => {
    void load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const res = await api.sendP2PMessage(tradeId, body);
      setMessages((m) => [...m, res.message]);
      setDraft('');
    } catch (e) {
      if (e instanceof LipaApiError) setDraft(body);
    } finally {
      setSending(false);
    }
  }, [draft, sending, tradeId]);

  return (
    <section className="rounded-card border border-border bg-surface shadow-card">
      <header className="border-b border-border px-4 py-3">
        <h2 className="text-sm font-medium text-ink">Chat with your counterparty</h2>
        <p className="mt-0.5 text-xs text-ink-subtle">
          Coordinate the payment here. Never share a code or account outside this trade.
        </p>
      </header>
      <div className="max-h-72 space-y-2 overflow-y-auto px-4 py-3">
        {messages.length === 0 && (
          <p className="py-6 text-center text-xs text-ink-subtle">No messages yet. Say hello.</p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                m.mine
                  ? 'bg-primary text-white'
                  : 'bg-surface-sunken text-ink'
              }`}
            >
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              <p className={`mt-0.5 text-micro ${m.mine ? 'text-white/70' : 'text-ink-subtle'}`}>
                {new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 border-t border-border p-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
          placeholder="Type a message"
          className="flex-1 rounded-field border border-border bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary"
        />
        <button
          disabled={sending || !draft.trim()}
          onClick={() => void send()}
          className="rounded-field bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </section>
  );
}

interface Step {
  label: string;
  done: boolean;
  active: boolean;
}

/** The step list, per path. Different lengths because the deals genuinely differ. */
function steps(path: Path, state: TradeStep['state'], role: 'buyer' | 'seller'): Step[] {
  const order: TradeStep['state'][] =
    path === 'rail_settled'
      ? ['locked_in_escrow', 'awaiting_payment', 'awaiting_release', 'released']
      : ['locked_in_escrow', 'awaiting_payment', 'payment_marked', 'awaiting_release', 'released'];

  const labels: Record<string, string> =
    path === 'rail_settled'
      ? {
          locked_in_escrow: 'Asset locked in escrow',
          awaiting_payment: role === 'buyer' ? 'Pay through VASP' : 'Buyer is paying',
          awaiting_release: 'We confirm your payment landed',
          released: role === 'buyer' ? 'Released to you' : 'Released to the buyer',
        }
      : {
          locked_in_escrow: 'Asset locked in escrow',
          awaiting_payment: role === 'buyer' ? 'Send the payment' : 'Buyer is paying',
          payment_marked: role === 'buyer' ? 'Mark as paid' : 'Buyer says they have paid',
          awaiting_release: role === 'seller' ? 'Confirm you received it' : 'Seller confirms',
          released: role === 'buyer' ? 'Released to you' : 'Released to the buyer',
        };

  const at = order.indexOf(state);
  return order.map((s, i) => ({
    label: labels[s] ?? s,
    done: at > i || state === 'released',
    active: at === i && state !== 'released',
  }));
}

export default function P2pTrade() {
  const { tradeId = '' } = useParams();

  // The path and the viewer's role come from the SERVER, derived there from the trade row. Reading
  // them from navigation state worked until somebody refreshed the page, and a screen that then
  // guessed would show the wrong deal, or show the seller's confirm button to the buyer.
  const { data, loading, error, reload } = useResource<P2PTrade>(
    () => api.p2pTrade(tradeId),
    [tradeId],
  );

  // Actions return the new state, so the screen advances without a refetch. Seeded from the server
  // once it arrives; `state` is null until then rather than defaulting to a step that may be wrong.
  const [state, setState] = useState<TradeStep['state'] | null>(null);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (data) setState(data.state);
  }, [data]);

  const act = useCallback(async (fn: () => Promise<TradeStep>) => {
    setPending(true);
    setNotice(null);
    try {
      const result = await fn();
      setState(result.state);
      // A refused settle is not an error: a gate said no and the trade is now a dispute with the
      // asset still held. The participant is told exactly that.
      if (result.reason) setNotice(result.reason);
    } catch (e) {
      setNotice(e instanceof LipaApiError ? e.message : 'That did not work.');
    } finally {
      setPending(false);
    }
  }, []);

  if (error) return <ErrorState body="We could not load this trade." onRetry={reload} />;
  if (loading || !data || state === null) return <LoadingCard lines={6} />;

  const path: Path = data.release_path;
  const role = data.viewer_role;
  const list = steps(path, state, role);
  const automatic = path === 'rail_settled';
  const disputed = state === 'under_dispute';
  const released = state === 'released';

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <header>
        <Link to="/p2p/trades" className="text-sm text-brand hover:underline">
          &larr; My trades
        </Link>
        <h1 className="mt-2 text-page-title text-ink">
          {automatic ? 'Settles automatically' : 'Seller confirms receipt'}
        </h1>
        <p className="tabular mt-1 text-sm font-medium text-ink">
          {data.amount} {data.asset}
        </p>
        <p className="mt-0.5 text-sm text-ink-muted">
          {automatic
            ? 'Your payment goes through us, so we can see it arrive. Nobody has to confirm.'
            : 'You pay the seller directly, so they have to confirm it arrived before the asset is released.'}
        </p>
      </header>

      {/* THE STEPS. Two lists, two lengths, one per path. */}
      <ol className="space-y-0 rounded-card border border-border bg-surface p-4 shadow-card">
        {list.map((s, i) => (
          <li key={s.label} className="flex gap-3 pb-4 last:pb-0">
            <div className="flex flex-col items-center">
              <span
                aria-hidden
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-micro font-bold ${
                  s.done
                    ? 'bg-success/15 text-success'
                    : s.active
                      ? 'bg-primary text-white'
                      : 'bg-surface-sunken text-ink-subtle'
                }`}
              >
                {s.done ? '✓' : i + 1}
              </span>
              {i < list.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
            </div>
            <p
              className={`pt-0.5 text-sm ${
                s.active ? 'font-medium text-ink' : s.done ? 'text-ink-muted' : 'text-ink-subtle'
              }`}
            >
              {s.label}
            </p>
          </li>
        ))}
      </ol>

      {/* The buyer↔seller room. Present on every open or settled trade so the two can coordinate. */}
      <ChatPanel tradeId={tradeId} />

      {disputed && (
        <div className="rounded-card border border-warning/30 bg-warning/5 p-4 text-sm">
          <p className="font-medium text-ink">This trade is in dispute</p>
          <p className="mt-1 text-ink-muted">
            {data.escrow_held
              ? 'The asset is still locked in escrow. It has not been released and it has not been returned. Somebody from our team will look at it and decide.'
              : 'This case has been decided and the escrow is no longer held.'}
          </p>
          <Link
            to={`/support?about=${tradeId}`}
            className="mt-3 inline-block text-sm text-brand hover:underline"
          >
            See the case
          </Link>
        </div>
      )}

      {released && (
        <div className="rounded-card border border-success/30 bg-success/5 p-4 text-sm">
          <p className="font-medium text-ink">Released</p>
          <p className="mt-1 text-ink-muted">
            The escrow has been released and the asset has moved.
          </p>
        </div>
      )}

      {notice && !disputed && (
        <div className="rounded-card border border-warning/30 bg-warning/5 p-3 text-sm text-ink">
          {notice}
        </div>
      )}

      {!released && !disputed && (
        <div className="space-y-2">
          {/*
            The manual path's buyer action. Deliberately worded as a CLAIM: it moves the trade room
            on and releases nothing, which is exactly what the backend does with it.
          */}
          {!automatic && role === 'buyer' && state === 'awaiting_payment' && (
            <button
              disabled={pending}
              onClick={() => act(() => api.markP2PPaid(tradeId))}
              className="w-full rounded-field bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              I have sent the payment
            </button>
          )}

          {!automatic && role === 'seller' && state === 'payment_marked' && (
            <button
              disabled={pending}
              onClick={() => act(() => api.confirmP2PReceipt(tradeId))}
              className="w-full rounded-field bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              I received the payment. Release the asset
            </button>
          )}

          {automatic && (
            <p className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
              Nothing to do here. We are watching for your payment and will release the asset the
              moment it lands.
            </p>
          )}

          <button
            disabled={pending}
            onClick={() => act(() => api.disputeP2PTrade(tradeId))}
            className="w-full rounded-field border border-border bg-surface px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-40"
          >
            Something is wrong. Raise a dispute
          </button>
        </div>
      )}

      {/*
        The sentence that stops the countdown being read as a promise. Both directions are somebody
        else's money: releasing on a timeout robs the seller, refunding on one robs the buyer.
      */}
      {!released && (
        <p className="text-center text-xs text-ink-subtle">
          If a step runs out of time, this becomes a dispute and the asset stays locked. It is never
          released or returned just because the clock ran out.
        </p>
      )}
    </div>
  );
}
